import { fetchBatchPlacesByIds, fetchPlaceAncestorsById, fetchPlaceByGeoCoords, fetchPlaceById, fetchPlaceByName } from 'actions/PlaceActions'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch, shallowEqual } from './reduxHooks'
import { getTypeaheadSuggestions } from 'api/search'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import GeoContext from 'contexts/geoContext'
import debounce from 'debounce-promise'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getPlaceKey } from 'lib/geo/geoUtils'

export function useLocalizedPlaces(placeIds: ReadonlyArray<string>): Record<string, App.Place | null | undefined> {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (placeIds.length > 0) {
      dispatch(fetchBatchPlacesByIds([...placeIds]))
    }
  }, [placeIds, dispatch])

  return useAppSelector((state) => {
    const result: Record<string, App.Place | null | undefined> = {}
    for (const id of placeIds) result[id] = state.destination.places[id]
    return result
  }, shallowEqual)
}

// Shared enrichment for `App.SearchItem` lists. Overrides `format.mainText` and
// `format.secondaryText` from the Place API cache so translated display wins across
// verticals (popular destinations, legacy HTTP typeahead). The socket-streaming path
// uses `getEnrichedTypeaheadStreamingData` for the richer `SearchItemV3` shape.
export function useLocalizedSearchItems<T extends App.SearchItem>(items: ReadonlyArray<T>): Array<T> {
  const placeIds = useMemo(
    () => items
      .filter(item => item.searchType === 'destination' && item.value && item.value !== ANYWHERE_PLACE_ID)
      .map(item => item.value),
    [items],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)
  return useMemo(
    () => items.map((item) => {
      const place = localizedPlaces[item.value]
      if (!place) return item
      return {
        ...item,
        format: {
          ...item.format,
          mainText: place.name,
          secondaryText: place.location ?? item.format.secondaryText,
        },
      }
    }),
    [items, localizedPlaces],
  )
}

export function useLocalizedBreadcrumbs(items: ReadonlyArray<App.BreadcrumbItem>): Array<App.BreadcrumbItem> {
  const placeIds = useMemo(
    () => items.map(item => item.placeId).filter((id): id is string => !!id),
    [items],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)
  return useMemo(
    () => items.map((item) => {
      const place = item.placeId ? localizedPlaces[item.placeId] : undefined
      return place ? { ...item, text: place.name } : item
    }),
    [items, localizedPlaces],
  )
}

export function usePlaceById(placeId = '', disable: boolean = false): App.Place | undefined {
  const place = useAppSelector(state => state.destination.places[placeId])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!disable && placeId) {
      dispatch(fetchPlaceById(placeId))
    }
  }, [placeId, disable, dispatch])

  return disable ? undefined : (place ?? undefined)
}

export function usePlaceByCoords(latitude?: number, longitude?: number, levels?: Array<App.PlaceType>): [App.Place | undefined, boolean] {
  const placeKey = getPlaceKey(latitude, longitude, levels)
  const place = useAppSelector(state => state.destination.coordsToPlace[placeKey])
  const fetching = useAppSelector(state => !!state.destination.placesLoading[placeKey])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (latitude && longitude) {
      dispatch(fetchPlaceByGeoCoords(latitude, longitude, { levels }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [placeKey])

  return [place ?? undefined, fetching]
}

export function usePlaceByName(name: string | undefined): App.Place | undefined {
  const place = useAppSelector(state => state.destination.locationToPlace[name ?? ''])
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (name) {
      dispatch(fetchPlaceByName(name))
    }
  }, [name, dispatch])
  return place ?? undefined
}

export function usePlacesAncestorsById(placeIds: Array<string>): [Array<Array<string>>, Array<boolean>] {
  const ancestorIdsList = useAppSelector(state => state.destination.placeAncestors ?? EmptyArray)
  const fetching = useAppSelector(state => state.destination.fetchingAncestors)
  const dispatch = useAppDispatch()
  const ancestorsList: Array<Array<string>> = useMemo(() => placeIds.map(id => ancestorIdsList[id] ?? []), [placeIds, ancestorIdsList])
  const fetchingList = useMemo<Array<boolean>>(() => placeIds.map(id => !!fetching[id]), [placeIds, fetching])
  useEffect(() => {
    for (const placeId of placeIds) {
      dispatch(fetchPlaceAncestorsById(placeId))
    }
  }, [placeIds, dispatch])

  return [ancestorsList, fetchingList]
}

export function usePlaceAncestorsById(placeId = ''): [Array<string>, boolean] {
  const placeIdList = useMemo(() => [placeId], [placeId])
  const [ancestorIdsList, fetchingList] = usePlacesAncestorsById(placeIdList)
  const [firstAncestor, firstFetching] = [ancestorIdsList[0], fetchingList[0]]
  return [firstAncestor, firstFetching]
}

interface PlaceBySearchPhraseOptions {
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  vertical?: App.SearchPlaceVertical
  disabled?: boolean
  priority?: App.SearchPlacePriority
  limit?: number
  canViewLuxPlusBenefits?: boolean
  rollout?: Array<string>
}

const EmptyObject = {}
export function usePlacesBySearchPhrase(
  phrase: string,
  options: PlaceBySearchPhraseOptions = EmptyObject,
): [Array<App.SearchItem>, boolean, boolean] {
  const { currentRegionCode } = useContext(GeoContext)
  const [fetching, setFetching] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const optionsRef = useRef<PlaceBySearchPhraseOptions>(options)
  optionsRef.current = options

  const getDebouncedPredictions = useMemo(
    () =>
      debounce((searchPhrase: string) => {
        if (searchPhrase.length >= 2) {
          return getTypeaheadSuggestions(searchPhrase, currentRegionCode, {
            types: optionsRef.current.placeTypes,
            searchTypes: optionsRef.current.searchTypes,
            vertical: optionsRef.current.vertical,
            priority: optionsRef.current.priority,
            limit: optionsRef.current.limit,
            canViewLuxPlusBenefits: optionsRef.current.canViewLuxPlusBenefits,
            rollout: optionsRef.current.rollout,
          })
            .then((searchItems) => {
              setResults(searchItems)
              setError(false)
            })
            .catch(() => setError(true))
            .finally(() => setFetching(false))
        } else {
          // search not long enough, means no results, so return empty result
          setFetching(false)
          return Promise.resolve([])
        }
      }, 200),
    [currentRegionCode],
  )

  useEffect(() => {
    if (!options.disabled) {
      setFetching(true)
      getDebouncedPredictions(phrase)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [phrase, options.disabled])

  const localizedResults = useLocalizedSearchItems(results)

  return [localizedResults, fetching, error]
}
